/**
 * Cart Drawer JavaScript
 * Handles cart drawer functionality with smooth animations
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cartDrawer');
    this.overlay = document.getElementById('cartDrawerOverlay');
    this.closeBtn = document.getElementById('closeCartDrawer');
    this.itemsContainer = document.getElementById('cartDrawerItems');

    // Performance optimizations
    this.isUpdating = false;
    this.updateQueue = null;
    this.debounceTimer = null;

    // Store instance reference for global access
    if (this.drawer) {
      this.drawer.__cartDrawer = this;
    }

    this.init();
  }

  init() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
        this.close();
      }
    });

    // Quantity and remove controls (using event delegation)
    this.initQuantityControls();

    // Order note
    this.initOrderNote();

    // Intercept all add to cart forms
    this.interceptAddToCart();
  }

  open() {
    this.drawer.classList.add('active');
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('active');
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  async refreshCart() {
    // Prevent multiple simultaneous updates
    if (this.isUpdating) {
      this.updateQueue = true;
      return;
    }

    this.isUpdating = true;
    this.showLoadingState();

    try {
      // Use Shopify Section Rendering API for faster updates
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      const sections = await response.json();

      if (sections['cart-drawer']) {
        // Use requestAnimationFrame for smooth DOM updates
        requestAnimationFrame(() => {
          // Parse the section HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');

          // Batch DOM updates to minimize reflows
          this.batchUpdate(() => {
            // Update the entire cart drawer content to ensure footer shows/hides correctly
            const newDrawer = doc.getElementById('cartDrawer');
            if (newDrawer && this.drawer) {
              // Save the current scroll position
              const scrollTop = this.itemsContainer?.scrollTop || 0;

              // Update entire drawer content
              this.drawer.innerHTML = newDrawer.innerHTML;

              // Re-initialize references since DOM was replaced
              this.itemsContainer = document.getElementById('cartDrawerItems');
              this.closeBtn = document.getElementById('closeCartDrawer');

              // Re-attach event listeners
              if (this.closeBtn) {
                this.closeBtn.addEventListener('click', () => this.close());
              }

              // Restore scroll position
              if (this.itemsContainer) {
                this.itemsContainer.scrollTop = scrollTop;
              }

              // Re-initialize quantity controls
              this.initQuantityControls();

              // Re-initialize order note
              this.initOrderNote();
            }

            // Update header cart count
            const newCount = doc.getElementById('cartItemCount');
            const headerCartCount = document.querySelector('.header-cart-count');
            if (headerCartCount && newCount) {
              headerCartCount.textContent = newCount.textContent;
            }
          });

          this.hideLoadingState();
          this.isUpdating = false;

          // Process queued update if any
          if (this.updateQueue) {
            this.updateQueue = false;
            this.refreshCart();
          }
        });
      }

    } catch (error) {
      console.error('Error refreshing cart:', error);
      this.hideLoadingState();
      this.isUpdating = false;
    }
  }

  // Batch DOM updates to minimize reflows
  batchUpdate(callback) {
    // Use opacity instead of display to maintain layout
    const oldOpacity = this.itemsContainer.style.opacity;
    const oldTransition = this.itemsContainer.style.transition;

    this.itemsContainer.style.transition = 'none';
    this.itemsContainer.style.opacity = '0';

    // Force a reflow
    void this.itemsContainer.offsetHeight;

    // Execute updates
    callback();

    // Restore with smooth fade-in
    requestAnimationFrame(() => {
      this.itemsContainer.style.transition = 'opacity 0.2s ease';
      this.itemsContainer.style.opacity = oldOpacity || '1';

      // Cleanup
      setTimeout(() => {
        this.itemsContainer.style.transition = oldTransition;
      }, 200);
    });
  }

  showLoadingState() {
    // Add loading indicator without blocking UI
    if (!this.loadingOverlay) {
      this.loadingOverlay = document.createElement('div');
      this.loadingOverlay.className = 'cart-loading-overlay';
      this.loadingOverlay.innerHTML = '<div class="cart-loading-spinner"></div>';
      this.drawer.appendChild(this.loadingOverlay);
    }
    this.loadingOverlay.classList.add('active');
  }

  hideLoadingState() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('active');
    }
  }

  initQuantityControls() {
    // Remove any existing listeners by cloning and replacing the element
    if (!this.itemsContainer) return;

    // Store handlers as instance methods if not already stored
    if (!this.clickHandler) {
      this.clickHandler = async (e) => {
        const target = e.target.closest('.quantity-decrease, .quantity-increase, .remove-btn');
        if (!target) return;

        // Prevent rapid clicks
        if (this.isUpdating) return;

        const line = target.dataset.line;

        if (target.classList.contains('quantity-decrease')) {
          const input = document.querySelector(`.quantity-input[data-line="${line}"]`);
          const currentQty = parseInt(input.value);
          if (currentQty > 1) {
            await this.updateQuantity(line, currentQty - 1);
          } else {
            await this.removeItem(line);
          }
        } else if (target.classList.contains('quantity-increase')) {
          const input = document.querySelector(`.quantity-input[data-line="${line}"]`);
          const currentQty = parseInt(input.value);
          await this.updateQuantity(line, currentQty + 1);
        } else if (target.classList.contains('remove-btn')) {
          await this.removeItem(line);
        }
      };

      this.changeHandler = async (e) => {
        if (e.target.classList.contains('quantity-input')) {
          if (this.isUpdating) return;

          const line = e.target.dataset.line;
          const newQty = parseInt(e.target.value);

          if (newQty === 0) {
            await this.removeItem(line);
          } else if (newQty > 0) {
            await this.updateQuantity(line, newQty);
          }
        }
      };
    }

    // Remove old listeners if they exist
    if (this.itemsContainer.dataset.listenersAttached) {
      this.itemsContainer.removeEventListener('click', this.clickHandler);
      this.itemsContainer.removeEventListener('change', this.changeHandler);
    }

    // Add listeners
    this.itemsContainer.addEventListener('click', this.clickHandler);
    this.itemsContainer.addEventListener('change', this.changeHandler);
    this.itemsContainer.dataset.listenersAttached = 'true';
  }

  initRemoveButtons() {
    // No longer needed - handled by event delegation in initQuantityControls
  }

  initOrderNote() {
    const cartNoteInput = document.getElementById('cartNote');
    if (cartNoteInput && !cartNoteInput.dataset.listenerAttached) {
      cartNoteInput.addEventListener('input', function() {
        clearTimeout(this.noteTimeout);
        this.noteTimeout = setTimeout(async () => {
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                note: this.value
              })
            });
          } catch (error) {
            console.error('Error saving note:', error);
          }
        }, 500);
      });
      cartNoteInput.dataset.listenerAttached = 'true';
    }
  }

  async updateQuantity(line, quantity) {
    // Prevent multiple simultaneous updates
    if (this.isUpdating) return;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(line),
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.refreshCart();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  async removeItem(line) {
    // Prevent multiple simultaneous updates
    if (this.isUpdating) return;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(line),
          quantity: 0
        })
      });

      if (response.ok) {
        await this.refreshCart();
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  interceptAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target;

      // Check if it's a product form
      if (form.getAttribute('action') && form.getAttribute('action').includes('/cart/add')) {
        e.preventDefault();

        const formData = new FormData(form);
        const submitButton = form.querySelector('[type="submit"]');
        const originalText = submitButton?.textContent;

        // Disable button
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Adding...';
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            this.refreshCart();
            this.open();
          } else {
            const error = await response.json();
            alert(error.description || 'Error adding to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Error adding to cart. Please try again.');
        } finally {
          // Re-enable button
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalText;
          }
        }
      }
    });

    // Global function for bundle add to cart
    window.refreshCartDrawer = () => {
      this.refreshCart();
      this.open();
    };
  }
}

// Initialize when DOM is ready - DISABLED FOR KACHING CART APP
// Uncomment below to re-enable custom cart drawer
/*
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawer();
  });
} else {
  new CartDrawer();
}
*/

// Global function to open cart drawer - with Kaching Cart support
window.openCartDrawer = function() {
  try {
    console.log('Opening Kaching Cart drawer...');

    // Method 1: Trigger Kaching's cart view directly
    // Kaching listens for this parameter on cart/add
    // We'll fetch the current cart and trigger Kaching's handler
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('Cart fetched, triggering Kaching handler');

        // Dispatch custom event that Kaching might listen for
        const kachingEvent = new CustomEvent('kaching:openCart', {
          bubbles: true,
          detail: { cart: cart }
        });
        window.dispatchEvent(kachingEvent);
        document.body.dispatchEvent(kachingEvent);
      });

    // Method 2: Try to find and click Kaching's cart icon/button
    const kachingSelectors = [
      '#kaching-cart-icon',
      '[data-kaching-cart-trigger]',
      '.kaching-cart-trigger',
      '[data-kaching-cart]',
      '.kaching-cart-icon',
      '[aria-label*="Kaching"]',
      'a[href*="kaching"]',
      'button[class*="kaching"]',
      '[id*="kaching-cart"]',
      '.kaching-app-cart-icon'
    ];

    for (const selector of kachingSelectors) {
      const kachingButton = document.querySelector(selector);
      if (kachingButton) {
        console.log('Found Kaching button with selector:', selector);

        // Try multiple click methods to ensure it works
        // Method 1: Direct click
        kachingButton.click();

        // Method 2: MouseEvent (simulates real user click)
        setTimeout(() => {
          const clickEvent = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true
          });
          kachingButton.dispatchEvent(clickEvent);
        }, 50);

        // Method 3: If button has href, navigate to it
        if (kachingButton.href) {
          setTimeout(() => {
            window.location.href = kachingButton.href;
          }, 100);
        }

        return;
      }
    }

    // Method 3: Use Kaching API if available
    if (window.KachingAppCart) {
      console.log('Using KachingAppCart API');

      if (typeof window.KachingAppCart.openCart === 'function') {
        window.KachingAppCart.openCart();
        return;
      }

      if (typeof window.KachingAppCart.showCart === 'function') {
        window.KachingAppCart.showCart();
        return;
      }

      if (typeof window.KachingAppCart.toggleCart === 'function') {
        window.KachingAppCart.toggleCart();
        return;
      }
    }

    // Method 4: Navigate to cart page as fallback
    console.log('Opening cart page');
    window.location.href = '/cart';

  } catch (error) {
    console.error('Error opening cart drawer:', error);
    window.location.href = '/cart';
  }
};

// Toggle cart options dropdown (order note + discount)
window.toggleCartOptions = function() {
  const dropdown = document.getElementById('cartOptionsDropdown');
  const chevron = document.getElementById('cartOptionsChevron');

  if (dropdown && chevron) {
    dropdown.classList.toggle('hidden');
    chevron.classList.toggle('rotate-180');
  }
};

// Apply discount code - Use Shopify's real discount system
window.applyDiscount = async function() {
  const discountInput = document.getElementById('discountCode');
  const discountCode = discountInput?.value.trim();

  if (!discountCode) {
    alert('Please enter a discount code');
    return;
  }

  const applyButton = document.querySelector('button[onclick="applyDiscount()"]');
  const originalText = applyButton?.textContent;
  if (applyButton) {
    applyButton.disabled = true;
    applyButton.textContent = 'Applying...';
  }

  try {
    // Apply discount via Shopify endpoint
    const response = await fetch(`/discount/${encodeURIComponent(discountCode)}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'text/html'
      }
    });

    // Wait for Shopify to process
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Check if discount was applied by fetching cart
    let retries = 0;
    let discountApplied = false;

    while (retries < 5 && !discountApplied) {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        discountApplied = true;
        break;
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      retries++;
    }

    // Refresh drawer
    const drawer = document.getElementById('cartDrawer')?.__cartDrawer;
    if (drawer) {
      await drawer.refreshCart();
    }

    // Clear input
    if (discountInput) {
      discountInput.value = '';
    }

    // Close options dropdown
    const dropdown = document.getElementById('cartOptionsDropdown');
    const chevron = document.getElementById('cartOptionsChevron');
    if (dropdown && chevron) {
      dropdown.classList.add('hidden');
      chevron.classList.remove('rotate-180');
    }

    if (!discountApplied) {
      alert('Discount code may be invalid or expired');
    }

  } catch (error) {
    console.error('Error applying discount:', error);
    alert('Error applying discount. Please try again.');
  } finally {
    if (applyButton) {
      applyButton.disabled = false;
      applyButton.textContent = originalText;
    }
  }
};

// Remove Shopify discount code
window.removeShopifyDiscount = async function(discountCode) {
  const removeButton = event?.target?.closest('button');

  if (removeButton) {
    removeButton.disabled = true;
  }

  try {
    // Try to clear from Shopify session
    await fetch('/discount/CLEAR', {
      method: 'GET',
      credentials: 'include',
      redirect: 'follow'
    });

    // Small delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Refresh drawer regardless
    const drawer = document.getElementById('cartDrawer')?.__cartDrawer;
    if (drawer) {
      await drawer.refreshCart();
    }
  } catch (error) {
    console.error('Error removing discount:', error);
    // Still try to refresh
    const drawer = document.getElementById('cartDrawer')?.__cartDrawer;
    if (drawer) {
      await drawer.refreshCart();
    }
  }
};

// Go to checkout (discount already applied to session)
window.goToCheckout = function() {
  window.location.href = '/checkout';
};

// Add recommended product to cart
window.addRecommendedProduct = async function(variantId) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (response.ok) {
      // Refresh cart to show the new item
      window.refreshCartDrawer();
    } else {
      const error = await response.json();
      alert(error.description || 'Error adding to cart');
    }
  } catch (error) {
    console.error('Error adding recommended product:', error);
    alert('Error adding to cart. Please try again.');
  }
};
